import { jsxs, jsx } from 'react/jsx-runtime';
import { Field, Select, Textarea, Input } from '@headlessui/react';
import { clsx } from 'clsx';
import { useRef, useCallback, useEffect } from 'react';
import { useFormState } from 'react-hook-form';
import { Icon } from '../Icon/Icon.js';
import '@react-icons/all-files/bs/BsArrowBarLeft.js';
import '@react-icons/all-files/bs/BsArrowBarRight.js';
import '@react-icons/all-files/bs/BsLayersFill.js';
import '@react-icons/all-files/bs/BsStars.js';
import '@react-icons/all-files/cg/CgSidebarOpen.js';
import '@react-icons/all-files/cg/CgSidebarRight.js';
import '@react-icons/all-files/fa/FaChevronDown.js';
import '@react-icons/all-files/fa/FaFileDownload.js';
import '@react-icons/all-files/fa/FaRedditSquare.js';
import '@react-icons/all-files/fa/FaSalesforce.js';
import '@react-icons/all-files/fa/FaSlack.js';
import '@react-icons/all-files/fa/FaTelegram.js';
import '@react-icons/all-files/fa6/FaSquareXTwitter.js';
import '@react-icons/all-files/fi/FiColumns.js';
import '@react-icons/all-files/md/MdAdd.js';
import '@react-icons/all-files/md/MdArrowBack.js';
import '@react-icons/all-files/md/MdArrowDropDown.js';
import '@react-icons/all-files/md/MdArrowForward.js';
import '@react-icons/all-files/md/MdBolt.js';
import '@react-icons/all-files/md/MdCheck.js';
import '@react-icons/all-files/md/MdClose.js';
import '@react-icons/all-files/md/MdDragIndicator.js';
import '@react-icons/all-files/md/MdEmail.js';
import '@react-icons/all-files/md/MdErrorOutline.js';
import '@react-icons/all-files/md/MdFileDownloadDone.js';
import '@react-icons/all-files/md/MdFilterList.js';
import '@react-icons/all-files/md/MdHelpOutline.js';
import '@react-icons/all-files/md/MdInfoOutline.js';
import '@react-icons/all-files/md/MdLanguage.js';
import '@react-icons/all-files/md/MdLink.js';
import '@react-icons/all-files/md/MdModeEditOutline.js';
import '@react-icons/all-files/md/MdMoreVert.js';
import '@react-icons/all-files/md/MdOutlineHistory.js';
import '@react-icons/all-files/md/MdOutlinePlaylistAdd.js';
import '@react-icons/all-files/md/MdOutlineSmartToy.js';
import '@react-icons/all-files/md/MdOutlineThumbDown.js';
import '@react-icons/all-files/md/MdOutlineThumbUp.js';
import '@react-icons/all-files/md/MdOutlineWarningAmber.js';
import '@react-icons/all-files/md/MdPerson.js';
import '@react-icons/all-files/md/MdPhone.js';
import '@react-icons/all-files/md/MdRemove.js';
import '@react-icons/all-files/md/MdShare.js';
import '@react-icons/all-files/pi/PiCalendarDotsLight.js';
import '@react-icons/all-files/pi/PiCloud.js';
import '@react-icons/all-files/pi/PiCloudFog.js';
import '@react-icons/all-files/pi/PiCloudLightning.js';
import '@react-icons/all-files/pi/PiCloudMoon.js';
import '@react-icons/all-files/pi/PiCloudRain.js';
import '@react-icons/all-files/pi/PiCloudSnow.js';
import '@react-icons/all-files/pi/PiCloudSun.js';
import '@react-icons/all-files/pi/PiDiamondsFour.js';
import '@react-icons/all-files/pi/PiFilePdf.js';
import '@react-icons/all-files/pi/PiListMagnifyingGlass.js';
import '@react-icons/all-files/pi/PiMapPinLineLight.js';
import '@react-icons/all-files/pi/PiMoon.js';
import '@react-icons/all-files/pi/PiMoonStars.js';
import '@react-icons/all-files/pi/PiSun.js';
import '@react-icons/all-files/pi/PiSunHorizon.js';
import '@react-icons/all-files/pi/PiTable.js';
import '@react-icons/all-files/pi/PiTabsFill.js';
import '@react-icons/all-files/pi/PiTag.js';
import '@react-icons/all-files/pi/PiWaveform.js';
import '@react-icons/all-files/rx/RxCopy.js';
import '@react-icons/all-files/rx/RxExternalLink.js';
import '@react-icons/all-files/vsc/VscSymbolKeyword.js';
import { useName } from '../utils/useName.js';
import { useFormField } from '../utils/useFormField.js';
import baseStyles from './InputBase.module.css.js';
import { InputBaseDescription } from './InputBaseDescription.js';
import { InputBaseLabel } from './InputBaseLabel.js';

function InputBase({ name: providedName, label, placeholder, description, rules, value, defaultValue, maxLength, disabled, onChange, type = "text", size = "base", autoComplete = "off", textFormatter, children, autoFocus, }) {
    const name = useName(providedName);
    const { register, setValue, formState: { errors }, control, } = useFormField();
    const touchedFields = useFormState({ name, control }).touchedFields;
    const previousValue = useRef(value);
    const handleChange = useCallback((event) => {
        if (value !== event.target.value) {
            const formattedValue = textFormatter ?
                textFormatter(event.target.value)
                : event.target.value;
            if (formattedValue !== event.target.value) {
                setValue(name, formattedValue, touchedFields[name] ? { shouldValidate: true } : undefined);
            }
            onChange === null || onChange === void 0 ? void 0 : onChange(formattedValue);
        }
    }, [name, onChange, setValue, value, textFormatter, touchedFields]);
    useEffect(() => {
        if (value !== undefined && value !== previousValue.current) {
            previousValue.current = value;
            setValue(name, value);
        }
    }, [name, setValue, value]);
    const error = errors[name];
    return (jsxs(Field, { disabled: disabled, children: [jsx(InputBaseLabel, { label: label, required: Boolean(rules === null || rules === void 0 ? void 0 : rules.required) }), type === "select" && (jsxs("div", { className: baseStyles.SelectWrapper, children: [jsx(Select, Object.assign({}, register(name, Object.assign(Object.assign({}, rules), { onChange: handleChange })), { className: clsx(baseStyles.Input, {
                            [baseStyles.error]: Boolean(error),
                            [baseStyles.small]: size === "small",
                            [baseStyles.base]: size === "base",
                        }), name: name, defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : value, autoFocus: autoFocus, children: children })), jsx("div", { className: baseStyles.SelectChevron, children: jsx(Icon, { name: "arrowDown" }) })] })), type === "textarea" && (jsx(Textarea, Object.assign({}, register(name, Object.assign(Object.assign({}, rules), { onChange: handleChange })), { className: clsx(baseStyles.Input, {
                    [baseStyles.error]: Boolean(error),
                    [baseStyles.small]: size === "small",
                    [baseStyles.base]: size === "base",
                }), name: name, defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : value, placeholder: placeholder, maxLength: maxLength, autoFocus: autoFocus }))), type !== "select" && type !== "textarea" && (jsx(Input, Object.assign({}, register(name, Object.assign(Object.assign({}, rules), { onChange: handleChange })), { className: clsx(baseStyles.Input, {
                    [baseStyles.error]: Boolean(error),
                    [baseStyles.small]: size === "small",
                    [baseStyles.base]: size === "base",
                }), name: name, defaultValue: defaultValue !== null && defaultValue !== void 0 ? defaultValue : value, type: type, placeholder: placeholder, maxLength: maxLength, autoComplete: autoComplete, autoFocus: autoFocus }))), jsx(InputBaseDescription, { description: description, error: error })] }));
}

export { InputBase };
//# sourceMappingURL=InputBase.js.map
